import React, {useState, useEffect} from "react";
import ModalComponent from '../ModalComponent';
import ControlGroup from "../forms/ControlGroup";
import {Button} from "react-bootstrap";
import {
  modals, customers, schoolYears, billingCategoryGroups, contacts as contactsDuck, billingConfigs as billingConfigsDuck
} from 'ducks';
import {singleDuck, duckify, filterDuck, dependentDuck} from "utils/duckify";
import Placeholder from '../Placeholder';
import ValidationErrors from '../forms/ValidationErrors';
import DatePicker from "react-datepicker";
import FontAwesome from 'react-fontawesome';
import PropTypes from 'prop-types';
import { BillingCategoryType } from '../../propTypes';
import moment from 'moment';
import {formatCurrency} from "../../utils";

const ManualPayment = (props) => {
  const {
    contactId, checklistAnswerId, customer, allDucksReady, financialContacts, backboneEventHandler,
    schoolYear, billingCategories, expectedAmount, currentPaymentAmount, defaultCategorizedAmounts, billingConfig, perOrder
  } = props;
  const paymentValues = customer.payment_config.payment_methods
  const paymentMethods = Object.values(paymentValues)
    .filter(method => method.enabled)
    .map(method => [method.display_name, method.canonical_name]) || []
  // We only send the display name to the backbone object, so this parses the canonical name out based on display name
  const paymentMatch = Object.values(paymentValues).find(obj => props.paymentMethod === obj.display_name);
  const currentPaymentMethod = paymentMatch ? paymentMatch.canonical_name : "";

  const [paymentAmount, setAmount] = useState(currentPaymentAmount || expectedAmount || 0);
  const [paymentMethod, setMethod] = useState(currentPaymentMethod || "");
  const [description, setDescription] = useState("");
  const [financialContactId, setFinancialContactId] = useState("");
  const [accountPerOrder, setAccountPerOrder] = useState(perOrder || []);
  const [completionDate, setCompletionDate] = useState(undefined);
  const [receivedDate, setReceivedDate] = useState(undefined);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [categorizedAmounts, setCategorizedAmounts] = useState(
    (defaultCategorizedAmounts && defaultCategorizedAmounts.length > 0) ? defaultCategorizedAmounts : null
  );

  const minDate = billingConfig && billingConfig.ledger_lock
    ? moment(billingConfig.ledger_lock).add(1, 'day').toDate()
    : undefined;

  const save = () => {
    setSaving(true)
    let currentErrors = checkErrors();

    if (Object.keys(currentErrors).length <= 0) {
      const {dispatch} = props;
      const token = $('meta[name="csrf-token"]').attr('content');

      fetch('/checklist_answers/' + checklistAnswerId, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json',  'X-CSRF-Token': token},
        credentials: 'include',
        body: JSON.stringify({
          description: description,
          payment_amount: paymentAmount,
          payment_method: paymentMethod,
          completion_date: completionDate,
          received_date: receivedDate,
          now_completion_date: !completionDate,
          financial_contact_id: financialContactId,
          categorized_amounts: categorizedAmounts,
          per_order: accountPerOrder
        })
      }).then((response) =>
        response.json()
          .then((responseData) => {
            backboneEventHandler.trigger('MODAL_SUBMIT', responseData);
            dispatch(modals.actions.hide())
            setSaving(false)
          }))
    } else {
      setSaving(false)
    }
  };

  const checkErrors = () => {
    let currentErrors = {};
    const electronicOrders = accountPerOrder.length > 0;

    if (schoolYear.billing_enabled && electronicOrders && accountPerOrder.find(a => !(a.financial_contact_id) || a.financial_contact_id.length <= 0)) {
      currentErrors = {...currentErrors, billing_account: ["for electronic payment not selected. Please choose an account and try again"]}
    }

    const total = categorizedAmounts ? categorizedAmounts.reduce(
      (accumulator, currentValue) => accumulator + parseFloat(currentValue.amount),
      0
    ) : parseFloat(paymentAmount);

    if (paymentMethod.length <= 0 && total > 0) {
      currentErrors = {
        ...currentErrors,
        payment_method: ["not selected. Please choose a payment method and try again"]
      };
    }

    if (total < 0) {
      currentErrors = {...currentErrors, negative_amount: ["not allowed. Amount must be 0 or greater"]}
    }

    if (schoolYear.billing_enabled && financialContactId.length <= 0 && (!electronicOrders || total > 0)) {
      currentErrors = {...currentErrors, billing_account: ["not selected. Please choose an account and try again"]}
    }

    // allow skipping billing category if we're not going to create items using it anyway
    if (categorizedAmounts && (categorizedAmounts.length > 1 || total > 0 || !electronicOrders)){
      categorizedAmounts.forEach((categorizedAmount) => {
        if (isNaN(categorizedAmount.amount)) {
          currentErrors = { ...currentErrors, amount: ['is not a number'] };
        }
        if (!categorizedAmount.billing_category_id) {
          currentErrors = {
            ...currentErrors,
            billing_category: ['not selected. Please choose a billing category and try again']
          };
        }
      });
    } else if (isNaN(paymentAmount)) {
      currentErrors = {...currentErrors, amount: ["is not a number"]}
    }
    setErrors(currentErrors);
    return currentErrors;
  };

  const buttons = [
    <Button variant="primary" disabled={saving}
            onClick={() => save()}>Submit</Button>,
  ];

  return (
    <ModalComponent title="Manual Payment" buttons={buttons}>
      <ValidationErrors errors={errors}/>
      <Placeholder.loading ready={allDucksReady}>
        {accountPerOrder.length > 0 && (
          <>
            <h2>Existing Electronic Payments</h2>
            {accountPerOrder.map((order) => (<>
              <b><a href={`/setup/manage_orders?search[id]=${order.order_id}`}
                    target='blank'>{formatCurrency(order.fee_amount)} paid by {order.fee_method.toLowerCase()}</a></b>
              {schoolYear.billing_enabled ?
                financialContacts.length > 0 ?
                  <div>
                    <div>
                      <ControlGroup label="Billing Account">
                        <select id={`billing_account_${order.order_id}`} className='form-select form-select-sm'
                                value={order.financial_contact_id}
                                onChange={(e) => {
                                  const hash = accountPerOrder;
                                  const oh = hash.find(o => o.order_id === order.order_id)
                                  oh.financial_contact_id = e.target.value;
                                  setAccountPerOrder([...hash]);
                                }}>
                          <option value={""}></option>
                          {financialContacts.map((contact) =>
                            <option value={contact.id}>{contact.first_name} {contact.last_name}</option>)}
                        </select>
                      </ControlGroup></div>
                  </div> :
                  <div>
                    <p>There are no billing accounts currently enabled.</p>
                    <span>Go <a href={'/contacts/' + contactId + '/households/account'} target='_blank'>here</a> to enable at least one account.</span>

                  </div>
                : ""
              }
            </>))}
            <h2>Additional Manual Payment (Optional)</h2>
          </>
        )}
        {categorizedAmounts && schoolYear.billing_enabled ? (
          <CategorizedAmounts
            categorizedAmounts={categorizedAmounts}
            setCategorizedAmounts={setCategorizedAmounts}
            billingCategories={billingCategories}
          />
        ) : (
          <ControlGroup label='Amount'>
            <input
              id='payment_amount'
              className='form-control form-control-sm'
              type='number'
              value={paymentAmount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </ControlGroup>
        )}
        <ControlGroup label="Payment Method">
          <select id='payment_method' className='form-select form-select-sm' value={paymentMethod} onChange={(e) => setMethod(e.target.value)}>
            <option value={""}></option>
            {paymentMethods.map(([displayName, canonicalName]) => (
              <option key={canonicalName} value={canonicalName}>{displayName}</option>
            ))}
          </select>
        </ControlGroup>

        {schoolYear.billing_enabled ?
          financialContacts.length > 0 ?
            <div>
              <ControlGroup label="Description (optional)">
                <input id='description' className='form-control form-control-sm' type={'text'} value={description}
                       onChange={(e) => setDescription(e.target.value)}/>
              </ControlGroup>
              <p> Choose the contact whose billing account this charge should appear under.</p>
              <div>
                <ControlGroup label="Billing Account">
                  <select id='billing_account' className='form-select form-select-sm' value={financialContactId}
                          onChange={(e) => setFinancialContactId(e.target.value)}>
                    <option value={""}></option>
                    {financialContacts.map((contact) =>
                      <option value={contact.id}>{contact.first_name} {contact.last_name}</option>)}
                  </select>
                </ControlGroup></div>
            </div> :
            <div>
              <p>There are no billing accounts currently enabled.</p>
              <span>Go <a href={'/contacts/' + contactId + '/households/account'} target='_blank'>here</a> to enable at least one account.</span>

            </div>
          : ""
        }

        <ControlGroup label={"Completion Date (optional)"}>
          <DatePicker
            className='form-control form-control-sm datepicker date-filter'
            selected={completionDate}
            onChange={setCompletionDate}
            minDate={minDate}
            name='completion_date'
            placeholder='Now'
          />
        </ControlGroup>

        <ControlGroup label={"Payment Received Date (optional)"}>
          <DatePicker
            className='form-control form-control-sm datepicker date-filter'
            selected={receivedDate}
            onChange={setReceivedDate}
            placeholder='(Same As Completion date)'
          />
        </ControlGroup>
      </Placeholder.loading>
    </ModalComponent>)

};

const CategorizedAmounts = ({ categorizedAmounts, setCategorizedAmounts, billingCategories }) => {
  const addAmount = () => {
    const amountsCopy = [...categorizedAmounts];
    amountsCopy.push({ amount: 0, billing_category_id: '' });
    setCategorizedAmounts(amountsCopy);
  };

  const removeAmount = (index) => {
    const amountsCopy = [...categorizedAmounts];
    amountsCopy.splice(index, 1);
    setCategorizedAmounts(amountsCopy);
  };

  const updateCategorizedAmountProp = (value, index, prop) => {
    const amountsCopy = [...categorizedAmounts];
    const current = categorizedAmounts[index];
    current[prop] = value;
    amountsCopy.splice(index, 1, current);
    setCategorizedAmounts(amountsCopy);
  };

  const updateAmount = (value, index) => {
    updateCategorizedAmountProp(value, index, 'amount');
  };

  const updateCategory = (value, index) => {
    updateCategorizedAmountProp(value, index, 'billing_category_id');
  };

  return (
    <div>
      {categorizedAmounts.map((amount, index) => (
        <div className='billing-item-amount' key={index}>
          {billingCategories.ready && index > 0 && (
            <button
              className='btn btn-sm btn-danger billing-item-editor-remove-button'
              title='Remove Split'
              onClick={() => removeAmount(index)}
            >
              <FontAwesome name='times' />
            </button>
          )}
          <ControlGroup label='Billing Category'>
            <Placeholder.select ready={billingCategories.ready}>
              <select
                id={`category-${index}`}
                className='form-select form-select-sm'
                value={categorizedAmounts[index].billing_category_id}
                onChange={(e) => updateCategory(e.target.value, index)}
              >
                <option value=''>Select category...</option>
                {
                  billingCategories.map(group => (
                    <optgroup key={group.id} label={group.name}>
                      <option value={group.id}>{group.name}</option>
                      {
                        group.children.filter(c => c.active).map(category => (
                          <option key={category.id} value={category.id}>{category.name}</option>
                        ))
                      }
                    </optgroup>
                  ))
                }
              </select>
            </Placeholder.select>
          </ControlGroup>
          <ControlGroup label='Amount'>
            <input
              id={`amount-${index}`}
              className='form-control form-control-sm'
              type='number'
              step='.01'
              value={categorizedAmounts[index].amount}
              onChange={(e) => updateAmount(e.target.value, index)}
            />
          </ControlGroup>
        </div>
      ))}
      <ControlGroup>
        <Button variant='success' size='sm' onClick={() => addAmount()}>Add Split</Button>
      </ControlGroup>
    </div>
  );
};
CategorizedAmounts.propTypes = {
  categorizedAmounts: PropTypes.arrayOf(PropTypes.shape({
    billing_category_id: PropTypes.string,
    amount: PropTypes.number
  })).isRequired,
  setCategorizedAmounts: PropTypes.func.isRequired,
  billingCategories: PropTypes.arrayOf(BillingCategoryType).isRequired
};

const mapDucksToProps = (ownProps) => {
  const schoolYear = singleDuck(schoolYears, ownProps.schoolYearId);

  return {
    customer: singleDuck(customers, 'current'),
    schoolYear,
    financialContacts: dependentDuck(schoolYear, ({ value: theSchoolYear }) => (
      theSchoolYear.billing_enabled && filterDuck(contactsDuck, { contact_id: ownProps.contactId, financial: true }))),
    billingCategories: dependentDuck(schoolYear, ({ value: theSchoolYear }) => (
      theSchoolYear.billing_enabled && filterDuck(billingCategoryGroups, { active: true }))),
    billingConfig: singleDuck(billingConfigsDuck, 'current')
  };
};


const ManualPaymentModal = duckify(mapDucksToProps)(ManualPayment);


export default ManualPaymentModal
